<template>
  <div>
    <vx-card title="" :is-filter="true" class="mb-8" :init-collapsed="true" :collapse-action="true" @keyup.enter="loadManagers(1, null, true)">
      <template slot="actions">
        <vs-button color="primary" type="filled" icon-pack="feather" icon="icon-plus" size="small" @click="createManager">{{ $t('add_manager') }}</vs-button>
      </template>

      <div class="vx-row">
        <div class="vx-col md:w-1/3 sm:w-1/3 w-full">
          <vs-input :placeholder="$t('name')" v-model="managerSearchParams.name" class="w-full"/>
        </div>
        <div class="vx-col md:w-1/3 sm:w-1/3 w-full">
          <vs-input :placeholder="$t('username')" v-model="managerSearchParams.username" class="w-full"/>
        </div>
        <div class="vx-col md:w-1/3 sm:w-1/3 w-full">
          <vs-input :placeholder="$t('context')" v-model="managerSearchParams.context" class="w-full"/>
        </div>
      </div>

      <template slot="footer">
        <vs-button color="success" type="filled" class="w-full" @click.prevent="loadManagers(1, null, true)" v-text="$t('search')"></vs-button>
      </template>
    </vx-card>

    <vx-card id="managerDataContainer" :title="$t('manager')" class="mb-8 vs-con-loading__container">
      <template slot="actions">
        <shared-management-tab-nav></shared-management-tab-nav>
      </template>

      <vs-table hover-flat v-if="managers && managers.length" :data="managers">
        <template slot="thead">
          <vs-th v-text="$t('no')"></vs-th>
          <vs-th v-text="$t('name')"></vs-th>
          <vs-th v-text="$t('username')"></vs-th>
          <vs-th v-text="$t('context')"></vs-th>
          <vs-th v-text="$t('mobile')"></vs-th>
          <vs-th v-text="$t('landline')"></vs-th>
          <vs-th class="text-center" v-text="$t('status')"></vs-th>
          <vs-th class="text-center" v-text="$t('action')"></vs-th>
        </template>

        <template slot-scope="{data}">
          <vs-tr :key="key" v-for="(tr, key) in data">
            <vs-td v-text="tr.count"></vs-td>
            <vs-td v-text="tr.name"></vs-td>
            <vs-td v-text="tr.username"></vs-td>
            <vs-td v-text="tr.context"></vs-td>
            <vs-td>
              <badge-clipboard v-if="tr.mobile" :text="tr.mobile"></badge-clipboard>
            </vs-td>
            <vs-td>
              <badge-clipboard v-if="tr.landline" :text="tr.landline"></badge-clipboard>
            </vs-td>
            <vs-td class="text-center">
              <badge-enabled :enabled="tr.is_active == 1"></badge-enabled>
            </vs-td>
            <vs-td class="text-center">
              <div class="flex justify-center">
                <vs-button color="primary" type="border" icon-pack="feather" icon="icon-edit" size="small" @click.prevent="editManager(tr.id)"></vs-button>&nbsp;
                <vs-button color="danger" type="filled" icon-pack="feather" icon="icon-trash-2" size="small" @click.prevent="deleteManager(tr.id)"></vs-button>&nbsp;
                <identity-login-button :id="tr.id" :username="tr.username"></identity-login-button>&nbsp;
                <reset-password-button :user-id="tr.id"></reset-password-button>
              </div>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
      <vs-alert v-else class="flex justify-center p-0 h-auto" color="danger" active="true">
        <div v-text="$t('no_record')"></div>
      </vs-alert>

      <template slot="footer">
        <paginate
          :total="managerPages"
          :itemPerPage="itemPerPage"
          :click-handler="loadManagers"
          v-model="initialPage"
          goto
        >
        </paginate>
      </template>
    </vx-card>

    <modal :title="$t('add_manager')" :active.sync="modals.add" size="lg">
      <div class="vx-row">
        <div class="vx-col md:w-1/2 sm:w-1/2 w-full">
          <div class="mb-3">
            <vs-input :label="$t('name')" :placeholder="$t('name')" v-model="managerFormData.name" class="w-full"/>
            <validation-error :model-error="managerFormErrors.name"></validation-error>
          </div>
          <div class="mb-3">
            <vs-input :label="$t('password')" type="password" :placeholder="$t('password')" v-model="managerFormData.password" class="w-full"/>
            <validation-error :model-error="managerFormErrors.password"></validation-error>
          </div>
          <div class="mb-3">
            <vs-input :label="$t('email')" :placeholder="$t('email')" v-model="managerFormData.email" class="w-full"/>
            <validation-error :model-error="managerFormErrors.email"></validation-error>
          </div>
        </div>

        <div class="vx-col md:w-1/2 sm:w-1/2 w-full">
          <div class="mb-3">
            <vs-input :label="$t('username')" :placeholder="$t('username')" v-model="managerFormData.username" class="w-full"/>
            <validation-error :model-error="managerFormErrors.username"></validation-error>
          </div>
          <div class="mb-3">
            <vs-input :label="$t('password_confirmation')" type="password" :placeholder="$t('password_confirmation')" v-model="managerFormData.password_confirmation" class="w-full"/>
            <validation-error :model-error="managerFormErrors.password_confirmation"></validation-error>
          </div>
          <div class="mb-3">
            <vs-input :label="$t('context')" :placeholder="$t('context')" v-model="managerFormData.context" class="w-full"/>
            <validation-error :model-error="managerFormErrors.context"></validation-error>
          </div>
        </div>

        <div class="vx-col w-full">
          <div class="mb-3">
            <label class="vs-input--label" v-text="$tc('department')"></label>
            <v-select :options="departmentOptions" :reduce="label => label.value" :placeholder="$tc('department')" v-model="managerFormData.department_id" class="md:mb-0" />
            <validation-error :model-error="managerFormErrors.department_id"></validation-error>
          </div>

          <div class="mb-3">
            <file-input :label="$t('upload_photo')" v-model="managerFormData.photo" :model="managerFormData.photo"></file-input>
            <validation-error :model-error="managerFormErrors.photo"></validation-error>
          </div>

          <div class="mb-3">
            <label class="vs-input--label" v-text="$t('company')"></label>
            <v-select :options="companyOptions" :reduce="label => label.value" :placeholder="$t('company')" v-model="managerFormData.company_id" class="md:mb-0" />
            <validation-error :model-error="managerFormErrors.company_id"></validation-error>
          </div>
        </div>
      </div>

      <vs-divider></vs-divider>

      <div class="flex justify-end">
        <vs-button id="addManagerButton" color="primary" type="filled" @click.prevent="submitManager">{{ $t('save') }}</vs-button>
      </div>
    </modal>

    <modal :title="$t('edit_manager')" :active.sync="modals.edit" size="lg">
      <div id="managerEditFormDataContainer" class="vs-con-loading__container">
        <div class="vx-row">
          <div class="vx-col md:w-1/2 sm:w-1/2 w-full">
            <div class="mb-3">
              <vs-input :label="$t('name')" :placeholder="$t('name')" v-model="managerFormData.name" class="w-full"/>
              <validation-error :model-error="managerFormErrors.name"></validation-error>
            </div>
            <div class="mb-3">
              <vs-input :label="$t('email')" :placeholder="$t('email')" v-model="managerFormData.email" class="w-full"/>
              <validation-error :model-error="managerFormErrors.email"></validation-error>
            </div>
            <div class="mb-3">
              <label class="vs-input--label" v-text="$tc('department')"></label>
              <v-select :options="departmentOptions" :reduce="label => label.value" :placeholder="$tc('department')" v-model="managerFormData.department_id" class="md:mb-0" />
              <validation-error :model-error="managerFormErrors.department_id"></validation-error>
            </div>
          </div>

          <div class="vx-col md:w-1/2 sm:w-1/2 w-full">
            <div class="mb-3">
              <vs-input :label="$t('username')" :placeholder="$t('username')" v-model="managerFormData.username" class="w-full"/>
              <validation-error :model-error="managerFormErrors.username"></validation-error>
            </div>
            <div class="mb-3">
              <vs-input :label="$t('context')" :placeholder="$t('context')" v-model="managerFormData.context" class="w-full"/>
              <validation-error :model-error="managerFormErrors.context"></validation-error>
            </div>
          </div>

          <div class="vx-col w-full">
            <div class="mb-3 flex flex-col" v-if="managerFormData.avatar_url != null">
              <label class="vs-input--label" v-text="$tc('current_avatar')"></label>
              <div>
                <img class="userAvatar" :src="managerFormData.avatar_url" alt="Photo">
              </div>
            </div>

            <div class="mb-3">
              <file-input :label="$t('upload_photo')" v-model="managerFormData.photo" :model="managerFormData.photo"></file-input>
              <validation-error :model-error="managerFormErrors.photo"></validation-error>
            </div>

            <div class="mb-3">
              <label class="vs-input--label" v-text="$t('company')"></label>
              <v-select :options="companyOptions" :reduce="label => label.value" :placeholder="$t('company')" v-model="managerFormData.company_id" class="md:mb-0" />
              <validation-error :model-error="managerFormErrors.company_id"></validation-error>
            </div>
          </div>
        </div>

        <vs-divider></vs-divider>

        <div class="flex justify-end">
          <vs-button id="editManagerButton" color="primary" type="filled" @click.prevent="updateManager">{{ $t('save') }}</vs-button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import SharedManagementTabNav from '@/views/Shared/Management/TabNav'
import Paginate from '@/components/Paginate'
import Modal from '@/components/Modal'
import IdentityLoginButton from '@/components/buttons/IdentityLoginButton'
import ResetPasswordButton from '@/components/buttons/ResetPasswordButton'
import Manager from '@/models/Manager'
import Pagination from '@/mixins/paginate'

export default {
  name: 'Admin.Management.Manager',
  mixins: [Pagination],
  components: {
    SharedManagementTabNav,
    Paginate,
    Modal,
    IdentityLoginButton,
    ResetPasswordButton
  },
  data () {
    return {
      managers: {},
      managerPages: 0,

      departmentOptions: [],
      companyOptions: [],

      managerFormData: {
        name: '',
        email: '',
        username: '',
        password: '',
        password_confirmation: '',
        context: '',
        language: '',
        is_active: 1,
        company_id: '',
        photo: {},
        departments: {}
      },
      managerFormErrors: [],
      itemPerPage: null,
      managerSearchParams: {
        name: '',
        username: '',
        context: ''
      },
      modals: {
        add: false,
        edit: false
      }
    }
  },
  watch: {
    'modals.add' (val) {
      if (!val) {
        this.resetManagerForm()
      }
    },
    'modals.edit' (val) {
      if (!val) {
        this.resetManagerForm()
      }
    }
  },
  mounted () {
    this.loadManagers(this.initialPage, null)
  },
  methods: {
    async loadManagers (page = 1, itemPerPage = null, backToPageOne = false) {
      this.$vs.loading({container: '#managerDataContainer'})
      if (itemPerPage !== null) {
        this.itemPerPage = itemPerPage
      } else if (this.itemPerPage !== null) {
        itemPerPage = this.itemPerPage
      }
      const response = await Manager.findAll({ ...this.managerSearchParams, page, itemPerPage })
      this.managers = response.data.data
      this.managerPages = response.data.meta.pagination.last_page
      this.companyOptions = response.data.meta.options.companies
      this.departmentOptions = response.data.meta.options.departments
      this.itemPerPage = response.data.meta.pagination.per_page
      if (backToPageOne === true) {
        this.initialPage = 1
      } else {
        this.initialPage = page
      }
      this.$vs.loading.close('#managerDataContainer > .con-vs-loading')
    },
    createManager () {
      this.modals.add = true
    },
    async submitManager () {
      this.$vs.loading({background: 'primary', color: '#fff', container: '#addManagerButton', scale: 0.45})

      try {
        const response = await Manager.store(this.managerFormData)

        this.$swalHelper.success({
          title: this.$t('action_success'),
          text: this.$t(response.data),
          confirmButton: this.$t('confirm')
        })

        this.modals.add = false

        this.loadManagers()
        this.resetManagerForm()
      } catch (e) {
        const errors = this.$swalHelper.displayCatchError({
          title: 'Error!',
          errorObject: e,
          confirmButton: this.$t('confirm')
        })
        this.managerFormErrors = errors.validationErrors
      }

      this.$vs.loading.close('#addManagerButton > .con-vs-loading')
    },
    async editManager (id) {
      this.$vs.loading({container: '#managerEditFormDataContainer'})

      try {
        this.modals.edit = true
        const response = await Manager.view({ id })
        this.managerFormData = response.data.manager
      } catch (e) {
        this.$swalHelper.displayCatchError({
          title: 'Error!',
          errorObject: e,
          confirmButton: this.$t('confirm')
        })
      }

      this.$vs.loading.close('#managerEditFormDataContainer > .con-vs-loading')
    },
    async updateManager () {
      this.$vs.loading({background: 'primary', color: '#fff', container: '#editManagerButton', scale: 0.45})

      try {
        const response = await Manager.update(this.managerFormData)

        this.$swalHelper.success({
          title: this.$t('action_success'),
          text: this.$t(response.data),
          confirmButton: this.$t('confirm')
        })

        this.modals.edit = false

        this.loadManagers()
        this.resetManagerForm()
      } catch (e) {
        const errors = this.$swalHelper.displayCatchError({
          title: 'Error!',
          errorObject: e,
          confirmButton: this.$t('confirm')
        })
        this.managerFormErrors = errors.validationErrors
      }

      this.$vs.loading.close('#editManagerButton > .con-vs-loading')
    },
    async deleteManager (id) {
      const confirm = await this.$swalHelper.warning({
        title: this.$t('are_you_sure_you_want_to_delete_the_user'),
        confirmButton: this.$t('execution'),
        cancelButton: this.$t('cancel')
      })

      if (confirm.value) {
        try {
          const response = await Manager.destroy({ id })

          this.$swalHelper.success({
            title: this.$t('action_success'),
            text: this.$t(response.data),
            confirmButton: this.$t('confirm')
          })

          this.loadManagers(1, null)
        } catch (e) {
          this.$swalHelper.displayCatchError({
            title: 'Error!',
            errorObject: e,
            confirmButton: this.$t('confirm')
          })
        }
      }
    },
    resetManagerForm () {
      this.managerFormData = {
        name: '',
        email: '',
        username: '',
        password: '',
        password_confirm: '',
        context: '',
        language: '',
        is_active: true,
        company_id: ''
      }
      this.managerFormErrors = []
    }
  }
}
</script>

<style lang="scss">
  #addManagerButton,
  #editManagerButton {
    .con-vs-loading {
      position: absolute !important;
    }
  }
  .swal2-popup {
    width: 33em !important;
  }
</style>
